import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, Upload, LogOut } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [newProduct, setNewProduct] = useState({ brand: '', product_name: '', weight_pack: '' });
  const [csvFile, setCsvFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  
  // Featured Brands State
  const [activeTab, setActiveTab] = useState('products'); // 'products' or 'featured-brands'
  const [featuredBrands, setFeaturedBrands] = useState([]);
  const [allBrands, setAllBrands] = useState([]);
  const [isAddFeaturedDialogOpen, setIsAddFeaturedDialogOpen] = useState(false);
  const [newFeaturedBrand, setNewFeaturedBrand] = useState({ brand_name: '', display_order: 1 });

  useEffect(() => {
    verifyAuth();
    fetchProducts();
    fetchFeaturedBrands();
    fetchAllBrands();
  }, []);

  const verifyAuth = async () => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin/login');
      return;
    }

    try {
      await axios.get(`${API}/auth/verify`, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (error) {
      localStorage.removeItem('admin_token');
      navigate('/admin/login');
    }
  };

  const getAuthHeaders = () => {
    const token = localStorage.getItem('admin_token');
    return { Authorization: `Bearer ${token}` };
  };

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/products`, { params: { limit: 5000 } });
      setProducts(response.data);
    } catch (error) {
      toast.error('Failed to fetch products');
      console.error('Fetch products error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddProduct = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/products`, newProduct, { headers: getAuthHeaders() });
      toast.success('Product added successfully');
      setNewProduct({ brand: '', product_name: '', weight_pack: '' });
      setIsAddDialogOpen(false);
      fetchProducts();
    } catch (error) {
      toast.error('Failed to add product');
      console.error('Add product error:', error);
    }
  };

  const handleEditProduct = async (e) => {
    e.preventDefault();
    try {
      await axios.put(
        `${API}/products/${editingProduct.id}`,
        editingProduct,
        { headers: getAuthHeaders() }
      );
      toast.success('Product updated successfully');
      setIsEditDialogOpen(false);
      setEditingProduct(null);
      fetchProducts();
    } catch (error) {
      toast.error('Failed to update product');
      console.error('Update product error:', error);
    }
  };

  const handleDeleteProduct = async (productId) => {
    if (!window.confirm('Are you sure you want to delete this product?')) return;

    try {
      await axios.delete(`${API}/products/${productId}`, { headers: getAuthHeaders() });
      toast.success('Product deleted successfully');
      fetchProducts();
    } catch (error) {
      toast.error('Failed to delete product');
      console.error('Delete product error:', error);
    }
  };

  const handleCsvUpload = async () => {
    if (!csvFile) {
      toast.error('Please select a file');
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', csvFile);

    try {
      const response = await axios.post(`${API}/admin/upload-csv`, formData, {
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'multipart/form-data'
        }
      });
      toast.success(response.data.message);
      setCsvFile(null);
      fetchProducts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload CSV');
      console.error('CSV upload error:', error);
    } finally {
      setUploading(false);
    }
  };

  // Featured Brands Functions
  const fetchAllBrands = async () => {
    try {
      const response = await axios.get(`${API}/products/brands`);
      setAllBrands(response.data.brands);
    } catch (error) {
      console.error('Error fetching all brands:', error);
    }
  };

  const fetchFeaturedBrands = async () => {
    try {
      const response = await axios.get(`${API}/featured-brands/all`, { headers: getAuthHeaders() });
      setFeaturedBrands(response.data);
    } catch (error) {
      console.error('Error fetching featured brands:', error);
    }
  };

  const handleAddFeaturedBrand = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/featured-brands`, newFeaturedBrand, { headers: getAuthHeaders() });
      toast.success('Featured brand added successfully');
      setNewFeaturedBrand({ brand_name: '', display_order: featuredBrands.length + 1 });
      setIsAddFeaturedDialogOpen(false);
      fetchFeaturedBrands();
    } catch (error) {
      toast.error('Failed to add featured brand');
      console.error('Add featured brand error:', error);
    }
  };

  const handleToggleFeaturedBrand = async (brandId, isActive) => {
    try {
      await axios.put(
        `${API}/featured-brands/${brandId}`,
        { is_active: !isActive },
        { headers: getAuthHeaders() }
      );
      toast.success('Featured brand updated');
      fetchFeaturedBrands();
    } catch (error) {
      toast.error('Failed to update featured brand');
      console.error('Update featured brand error:', error);
    }
  };

  const handleDeleteFeaturedBrand = async (brandId) => {
    if (!window.confirm('Are you sure you want to remove this featured brand?')) return;

    try {
      await axios.delete(`${API}/featured-brands/${brandId}`, { headers: getAuthHeaders() });
      toast.success('Featured brand removed successfully');
      fetchFeaturedBrands();
    } catch (error) {
      toast.error('Failed to remove featured brand');
      console.error('Delete featured brand error:', error);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    navigate('/admin/login');
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-[#0b1f3f] text-white shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-[#fcce32] rounded-lg flex items-center justify-center">
                <span className="text-[#0b1f3f] font-bold text-xl">SFH</span>
              </div>
              <div>
                <h1 className="text-2xl font-bold" data-testid="dashboard-heading">Admin Dashboard</h1>
                <p className="text-sm text-gray-300">Sai Food House</p>
              </div>
            </div>
            <Button
              onClick={handleLogout}
              variant="outline"
              className="text-white border-white hover:bg-white hover:text-[#0b1f3f]"
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Tabs */}
        <div className="mb-6">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8">
              <button
                onClick={() => setActiveTab('products')}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'products'
                    ? 'border-[#fcce32] text-[#0b1f3f]'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Products Management
              </button>
              <button
                onClick={() => setActiveTab('featured-brands')}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'featured-brands'
                    ? 'border-[#fcce32] text-[#0b1f3f]'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Featured Brands
              </button>
            </nav>
          </div>
        </div>

        {/* Products Tab */}
        {activeTab === 'products' && (
          <>
        {/* Actions Bar */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-3">Quick Actions</h3>
              <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
                <DialogTrigger asChild>
                  <Button className="bg-[#fcce32] text-[#0b1f3f] hover:bg-[#e0b82a]" data-testid="add-product-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    Add New Product
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Add New Product</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleAddProduct} className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium mb-1">Brand</label>
                      <Input
                        value={newProduct.brand}
                        onChange={(e) => setNewProduct({ ...newProduct, brand: e.target.value })}
                        placeholder="Brand name"
                        required
                        data-testid="add-brand-input"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Product Name</label>
                      <Input
                        value={newProduct.product_name}
                        onChange={(e) => setNewProduct({ ...newProduct, product_name: e.target.value })}
                        placeholder="Product name"
                        required
                        data-testid="add-product-name-input"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Weight/Pack</label>
                      <Input
                        value={newProduct.weight_pack}
                        onChange={(e) => setNewProduct({ ...newProduct, weight_pack: e.target.value })}
                        placeholder="e.g., 1KG"
                        required
                        data-testid="add-weight-input"
                      />
                    </div>
                    <Button type="submit" className="w-full bg-[#fcce32] text-[#0b1f3f] hover:bg-[#e0b82a]" data-testid="save-product-btn">
                      Save Product
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-3">Bulk Upload</h3>
              <div className="flex gap-2">
                <Input
                  type="file"
                  accept=".csv,.xlsx,.xls"
                  onChange={(e) => setCsvFile(e.target.files[0])}
                  className="flex-1"
                  data-testid="csv-upload-input"
                />
                <Button
                  onClick={handleCsvUpload}
                  disabled={!csvFile || uploading}
                  className="bg-[#0b1f3f] text-white hover:bg-[#1a3a5c]"
                  data-testid="upload-csv-btn"
                >
                  <Upload className="w-4 h-4 mr-2" />
                  {uploading ? 'Uploading...' : 'Upload'}
                </Button>
              </div>
              <p className="text-xs text-gray-500 mt-2">
                Upload CSV/Excel with columns: BRAND/COMPANY, PRODUCT NAME, WEIGHT/PACK
              </p>
            </div>
          </div>
        </div>

        {/* Products Table */}
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="p-6 border-b">
            <h2 className="text-xl font-bold text-gray-800">Products ({products.length})</h2>
          </div>
          
          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-4 border-[#fcce32] mx-auto"></div>
              <p className="mt-4 text-gray-600">Loading products...</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="font-bold">Brand</TableHead>
                    <TableHead className="font-bold">Product Name</TableHead>
                    <TableHead className="font-bold">Weight/Pack</TableHead>
                    <TableHead className="font-bold text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {products.map((product, index) => (
                    <TableRow key={product.id} data-testid={`product-row-${index}`}>
                      <TableCell className="font-medium">{product.brand}</TableCell>
                      <TableCell>{product.product_name}</TableCell>
                      <TableCell>{product.weight_pack}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Dialog open={isEditDialogOpen && editingProduct?.id === product.id} onOpenChange={(open) => {
                            setIsEditDialogOpen(open);
                            if (!open) setEditingProduct(null);
                          }}>
                            <DialogTrigger asChild>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setEditingProduct(product)}
                                data-testid={`edit-product-btn-${index}`}
                              >
                                <Edit className="w-4 h-4" />
                              </Button>
                            </DialogTrigger>
                            <DialogContent>
                              <DialogHeader>
                                <DialogTitle>Edit Product</DialogTitle>
                              </DialogHeader>
                              {editingProduct && (
                                <form onSubmit={handleEditProduct} className="space-y-4">
                                  <div>
                                    <label className="block text-sm font-medium mb-1">Brand</label>
                                    <Input
                                      value={editingProduct.brand}
                                      onChange={(e) => setEditingProduct({ ...editingProduct, brand: e.target.value })}
                                      placeholder="Brand name"
                                      required
                                      data-testid="edit-brand-input"
                                    />
                                  </div>
                                  <div>
                                    <label className="block text-sm font-medium mb-1">Product Name</label>
                                    <Input
                                      value={editingProduct.product_name}
                                      onChange={(e) => setEditingProduct({ ...editingProduct, product_name: e.target.value })}
                                      placeholder="Product name"
                                      required
                                      data-testid="edit-product-name-input"
                                    />
                                  </div>
                                  <div>
                                    <label className="block text-sm font-medium mb-1">Weight/Pack</label>
                                    <Input
                                      value={editingProduct.weight_pack}
                                      onChange={(e) => setEditingProduct({ ...editingProduct, weight_pack: e.target.value })}
                                      placeholder="e.g., 1KG"
                                      required
                                      data-testid="edit-weight-input"
                                    />
                                  </div>
                                  <Button type="submit" className="w-full bg-[#fcce32] text-[#0b1f3f] hover:bg-[#e0b82a]" data-testid="update-product-btn">
                                    Update Product
                                  </Button>
                                </form>
                              )}
                            </DialogContent>
                          </Dialog>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleDeleteProduct(product.id)}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            data-testid={`delete-product-btn-${index}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </div>
        </>
        )}

        {/* Featured Brands Tab */}
        {activeTab === 'featured-brands' && (
          <div className="space-y-6">
            {/* Header */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-2xl font-bold text-[#0b1f3f] mb-4">Manage Featured Brands</h2>
              <p className="text-gray-600 mb-4">
                Control which brands appear in the "Famous Brands in Our Catalog" section on the homepage. 
                If no brands are featured, the system will automatically show top brands by product count.
              </p>
              
              <Dialog open={isAddFeaturedDialogOpen} onOpenChange={setIsAddFeaturedDialogOpen}>
                <DialogTrigger asChild>
                  <Button className="bg-[#fcce32] text-[#0b1f3f] hover:bg-[#e0b82a]">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Featured Brand
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Add Featured Brand</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleAddFeaturedBrand} className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium mb-1">Brand Name</label>
                      <select
                        value={newFeaturedBrand.brand_name}
                        onChange={(e) => setNewFeaturedBrand({ ...newFeaturedBrand, brand_name: e.target.value })}
                        className="w-full h-10 px-3 border border-gray-300 rounded-md"
                        required
                      >
                        <option value="">Select a brand</option>
                        {allBrands.map((brand) => (
                          <option key={brand} value={brand}>
                            {brand}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Display Order</label>
                      <Input
                        type="number"
                        value={newFeaturedBrand.display_order}
                        onChange={(e) => setNewFeaturedBrand({ ...newFeaturedBrand, display_order: parseInt(e.target.value) })}
                        placeholder="1"
                        min="1"
                        required
                      />
                      <p className="text-xs text-gray-500 mt-1">Lower numbers appear first</p>
                    </div>
                    <Button type="submit" className="w-full bg-[#fcce32] text-[#0b1f3f] hover:bg-[#e0b82a]">
                      Add Brand
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>

            {/* Featured Brands List */}
            <div className="bg-white rounded-lg shadow-md overflow-hidden">
              <div className="p-6 border-b">
                <h3 className="text-xl font-bold text-gray-800">Featured Brands ({featuredBrands.length})</h3>
                <p className="text-sm text-gray-600 mt-1">
                  {featuredBrands.length === 0 
                    ? 'No featured brands set. Homepage will show automatic top brands.'
                    : 'These brands will appear on the homepage in the order below.'}
                </p>
              </div>
              
              {featuredBrands.length === 0 ? (
                <div className="text-center py-12">
                  <p className="text-gray-600 mb-4">No featured brands added yet</p>
                  <Button 
                    onClick={() => setIsAddFeaturedDialogOpen(true)}
                    className="bg-[#fcce32] text-[#0b1f3f] hover:bg-[#e0b82a]"
                  >
                    Add Your First Featured Brand
                  </Button>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="font-bold">Order</TableHead>
                      <TableHead className="font-bold">Brand Name</TableHead>
                      <TableHead className="font-bold">Status</TableHead>
                      <TableHead className="font-bold text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {featuredBrands.map((brand, index) => (
                      <TableRow key={brand.id}>
                        <TableCell className="font-medium">{brand.display_order}</TableCell>
                        <TableCell className="font-semibold text-[#0b1f3f]">{brand.brand_name}</TableCell>
                        <TableCell>
                          <span className={`px-2 py-1 text-xs rounded-full ${
                            brand.is_active 
                              ? 'bg-green-100 text-green-800' 
                              : 'bg-gray-100 text-gray-800'
                          }`}>
                            {brand.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleToggleFeaturedBrand(brand.id, brand.is_active)}
                            >
                              {brand.is_active ? 'Deactivate' : 'Activate'}
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleDeleteFeaturedBrand(brand.id)}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
